// Section 22: the persistent header. Year and season, population, the fragility index with its
// factors, the steady-state counter ("Stable: 3 of 10 years") with the last failed condition on
// hover, observer controls, the menu, and End season (disabled with a reason while a question
// is unanswered or the season is resolving).
import type { PromptId } from '../../sim/api';
import type { BlockReason, HeaderVM } from '../../viewModels';
import { seasonName } from '../format';
import { text, uiText } from '../text/en';
import { Num } from '../tooltip/num';
import { Tooltip } from '../tooltip/tooltip';
import { Menu, type MenuActions } from './menu';

interface Props {
  vm: HeaderVM;
  observerRunning: boolean;
  canSave: boolean;
  menu: MenuActions;
  onEndSeason: () => void;
  onOpenPrompt: (id: PromptId) => void;
  onPlay: () => void;
  onPause: () => void;
  onStep: () => void;
}

export function blockText(b: BlockReason): string {
  const t = uiText.blocked;
  switch (b.code) {
    case 'unanswered':
      return t.unanswered(b.count);
    case 'invalid':
      return b.message;
    case 'ended':
      return t.ended;
    case 'title':
    case 'reading-report':
    case 'resolving':
    case 'paused':
      return t[b.code];
  }
}

function SteadyCounter({ vm }: { vm: HeaderVM }) {
  const t = uiText.header;
  const text = t.stable(vm.steadyCounter, vm.steadyTarget);
  return (
    <Tooltip
      label={text}
      content={() => (
        <span class="steady-tip">
          {vm.lastConditions.length === 0 ? (
            <span>{t.neverChecked}</span>
          ) : (
            <ul class="steady-tip__list">
              {vm.lastConditions.map((c) => (
                <li key={c.id} class={c.pass ? 'is-pass' : 'is-fail'}>
                  <span aria-hidden="true">{c.pass ? '✓ ' : '✗ '}</span>
                  {uiText.steady[c.id] ?? c.id}
                  <span class="visually-hidden">{c.pass ? ' (held)' : ' (failed)'}</span>
                </li>
              ))}
            </ul>
          )}
          {vm.lastFailed !== null && (
            <span class="steady-tip__failed">
              {t.lastFailed}: {uiText.steady[vm.lastFailed] ?? vm.lastFailed}
            </span>
          )}
        </span>
      )}
    >
      {text}
    </Tooltip>
  );
}

export function Header(props: Props) {
  const { vm } = props;
  const t = uiText.header;
  const firstPrompt = vm.unanswered[0];
  return (
    <header class="header">
      <div class="header__brand">
        <span class="header__name">{text.title}</span>
        <span class="header__seed hint">
          {uiText.app.seed} <span class="num">{vm.seed}</span>
          {vm.ironman && <span class="tag"> {t.ironman}</span>}
          {vm.sandbox && <span class="tag"> {t.sandbox}</span>}
        </span>
      </div>
      <dl class="header__stats">
        <div>
          <dt>{t.season}</dt>
          <dd>
            {seasonName(vm.season)}, {t.year} <span class="num">{vm.year}</span>
          </dd>
        </div>
        <div>
          <dt>{t.population}</dt>
          <dd>
            <Num value={vm.population} format="whole" factors={vm.populationFactors} label={t.population} />
          </dd>
        </div>
        <div>
          <dt>{t.fragility}</dt>
          <dd>
            {vm.fragilityPending ? (
              <span class="num hint" title={t.fragilityPending}>
                …<span class="visually-hidden"> {t.fragilityPending}</span>
              </span>
            ) : (
              <Num
                value={vm.fragility}
                format="whole"
                factors={vm.fragilityFactors}
                label={t.fragility}
              />
            )}
            {vm.fragilityForecast && <span class="hint"> {t.fragilityForecast}</span>}
          </dd>
        </div>
        <div>
          <dt>{t.steady}</dt>
          <dd>
            <SteadyCounter vm={vm} />
          </dd>
        </div>
      </dl>
      {vm.observer && (
        <div class="header__observer" role="group" aria-label={t.observer}>
          {props.observerRunning ? (
            <button type="button" class="button" onClick={props.onPause}>
              {t.pause}
            </button>
          ) : (
            <button type="button" class="button" onClick={props.onPlay} disabled={vm.outcome !== null && !vm.sandbox}>
              {t.play}
            </button>
          )}
          <button type="button" class="button" onClick={props.onStep} disabled={props.observerRunning}>
            {t.step}
          </button>
        </div>
      )}
      <Menu {...props.menu} canSave={props.canSave} />
      <div class="header__action">
        <button
          type="button"
          class="button button--primary"
          disabled={!vm.canEndSeason}
          aria-describedby={vm.blocked === null ? undefined : 'end-season-reason'}
          onClick={props.onEndSeason}
        >
          {t.endSeason}
        </button>
        {vm.blocked !== null && (
          <p id="end-season-reason" class="hint header__reason">
            {blockText(vm.blocked)}{' '}
            {vm.blocked.code === 'unanswered' && firstPrompt !== undefined && (
              <button type="button" class="link link--inline" onClick={() => props.onOpenPrompt(firstPrompt)}>
                {uiText.report.answer}
              </button>
            )}
          </p>
        )}
      </div>
    </header>
  );
}
